import { useEffect, useState, useRef, useCallback } from 'react';
import { api, ApiError } from '../lib/api';
import type { TemplateView, TemplateMeta } from '../lib/types';
import {
  EVENT_TOKENS, EVENT_LABEL, CHANNEL_LABEL, renderPreview,
  CHANNEL_HAS_SUBJECT, CHANNEL_IS_HTML, type TokenInfo,
} from '../lib/templates';
import { Modal, ErrorBar } from './InstitutionsPage';

// channels an admin can meaningfully edit here (Push has no mobile app yet)
const EDITABLE_CHANNELS = ['Email', 'Sms', 'InApp'];

export function TemplatesPage({ embedded = false }: { embedded?: boolean }) {
  const [meta, setMeta] = useState<TemplateMeta | null>(null);
  const [templates, setTemplates] = useState<TemplateView[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ event: string; channel: string; existing?: TemplateView } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        api.get<TemplateMeta>('/api/notification-templates/meta'),
        api.get<TemplateView[]>('/api/notification-templates'),
      ]);
      setMeta(m);
      setTemplates(t);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const find = (event: string, channel: string) =>
    templates.find((t) => t.event === event && t.channel === channel);

  // only show events we have a known token vocabulary for
  const events = (meta?.events ?? []).filter((e) => EVENT_TOKENS[e]);

  return (
    <div>
      {!embedded && (
        <div style={{ marginBottom: 22 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Admin</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Message templates</h1>
          <p style={{ color: 'var(--ink-2)', marginTop: 4, maxWidth: 620 }}>
            Customise the wording your institution sends. Any cell left as{' '}
            <span className="pill pill-muted" style={{ padding: '1px 8px' }}>Default</span>{' '}
            uses Eduvora’s built-in message until you set your own.
          </p>
        </div>
      )}

      {loading && <p style={{ color: 'var(--ink-3)' }}>Loading…</p>}

      {!loading && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '.02em' }}>Event</th>
                {EDITABLE_CHANNELS.map((c) => (
                  <th key={c} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '.02em' }}>
                    {CHANNEL_LABEL[c]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event} style={{ borderBottom: '1px solid var(--line-2)' }}>
                  <td style={{ padding: '14px 16px', fontWeight: 600 }}>{EVENT_LABEL[event] ?? event}</td>
                  {EDITABLE_CHANNELS.map((channel) => {
                    const t = find(event, channel);
                    return (
                      <td key={channel} style={{ padding: '10px 16px' }}>
                        <button className="btn" onClick={() => setEditing({ event, channel, existing: t })}
                          style={{ padding: '6px 12px', fontSize: 13 }}>
                          {t
                            ? <span className="pill pill-ok" style={{ padding: '1px 8px', marginRight: 4 }}>{t.isActive ? 'Custom' : 'Off'}</span>
                            : <span className="pill pill-muted" style={{ padding: '1px 8px', marginRight: 4 }}>Default</span>}
                          Edit
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <TemplateEditor
          event={editing.event}
          channel={editing.channel}
          existing={editing.existing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TemplateEditor({ event, channel, existing, onClose, onSaved }: {
  event: string; channel: string; existing?: TemplateView; onClose: () => void; onSaved: () => void;
}) {
  const tokens: TokenInfo[] = EVENT_TOKENS[event] ?? [];
  const hasSubject = CHANNEL_HAS_SUBJECT(channel);
  const isHtml = CHANNEL_IS_HTML(channel);

  const [subject, setSubject] = useState(existing?.subject ?? '');
  const [body, setBody] = useState(existing?.body ?? '');
  const [isActive, setIsActive] = useState(existing?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const insertToken = (token: string) => {
    const el = bodyRef.current;
    const ins = `{{${token}}}`;
    if (!el) { setBody((b) => b + ins); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + ins + body.slice(end));
    // restore focus just after the inserted token
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + ins.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const save = async () => {
    setError(null); setBusy(true);
    try {
      if (existing) {
        await api.put(`/api/notification-templates/${existing.id}`, { subject: hasSubject ? subject : null, body, isActive });
      } else {
        await api.post('/api/notification-templates', { event, channel, subject: hasSubject ? subject : null, body });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the template.');
    } finally { setBusy(false); }
  };

  const ready = body.trim().length > 0 && (!hasSubject || subject.trim().length > 0);

  const previewSubject = renderPreview(subject, tokens);
  const previewBody = renderPreview(body, tokens);

  return (
    <Modal title={`${EVENT_LABEL[event] ?? event} · ${CHANNEL_LABEL[channel]}`} onClose={onClose}>
      {error && <ErrorBar>{error}</ErrorBar>}

      {/* token chips */}
      <label className="label">Insert a field</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {tokens.map((t) => (
          <button key={t.token} onClick={() => insertToken(t.token)} title={`e.g. ${t.sample}`}
            className="mono" style={{
              border: '1px solid var(--line)', background: 'var(--surface-2)', color: 'var(--accent-ink)',
              borderRadius: 999, padding: '3px 10px', fontSize: 12, cursor: 'pointer',
            }}>
            {`{{${t.token}}}`}
          </button>
        ))}
      </div>

      {hasSubject && (
        <>
          <label className="label">Subject</label>
          <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. Payment received — {{tenantName}}" style={{ marginBottom: 14 }} />
        </>
      )}

      <label className="label">Message {isHtml && <span style={{ color: 'var(--ink-3)', textTransform: 'none', fontWeight: 400 }}>· HTML allowed</span>}</label>
      <textarea ref={bodyRef} className="input mono" value={body} onChange={(e) => setBody(e.target.value)}
        rows={isHtml ? 7 : 4} placeholder={isHtml ? '<p>Dear {{guardianName}}, …</p>' : 'Short text with {{tokens}}'}
        style={{ resize: 'vertical', lineHeight: 1.5, marginBottom: 14 }} />

      {/* live preview */}
      {/* Preview renders the admin's OWN template with SAMPLE values — never end-user
          data — so dangerouslySetInnerHTML here is not an injection surface. The real
          email send happens server-side; this is a trusted-author preview only. */}
      <label className="label">Preview <span style={{ color: 'var(--ink-3)', textTransform: 'none', fontWeight: 400 }}>· with sample values</span></label>
      <div className="card" style={{ padding: 14, background: 'var(--surface-2)', marginBottom: 16 }}>
        {hasSubject && previewSubject && (
          <div style={{ fontWeight: 600, marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid var(--line)' }}>{previewSubject}</div>
        )}
        {isHtml
          ? <div style={{ fontSize: 14, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: previewBody || '<span style="color:var(--ink-3)">Nothing to preview yet.</span>' }} />
          : <div style={{ fontSize: 14, lineHeight: 1.6, color: previewBody ? 'var(--ink)' : 'var(--ink-3)' }}>{previewBody || 'Nothing to preview yet.'}</div>}
      </div>

      {/* active toggle (only meaningful for existing) */}
      {existing && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, cursor: 'pointer' }}>
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          <span style={{ fontSize: 14 }}>
            Active {isActive
              ? <span style={{ color: 'var(--ink-3)' }}>— this custom message is in use</span>
              : <span style={{ color: 'var(--ink-3)' }}>— falls back to the default message</span>}
          </span>
        </label>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={!ready || busy} onClick={save}>
          {busy ? 'Saving…' : existing ? 'Save changes' : 'Create template'}
        </button>
      </div>
    </Modal>
  );
}
